import React, { useEffect, useRef, useState } from "react";
import { sendBioMessage } from "@/services/bioLeadService";
import { replyOf } from "@/components/BioMessageSheet";
import { safeHref } from "@/components/AgentBioAbout";
import "./WantContactPanel.scss";

const FIELD_KEYS = ["name", "email", "phone", "message", "consent"];
const EMPTY = { name: "", email: "", phone: "", address: "", message: "", website: "", consent: false };

export const ownerMessageOf = (title) =>
  title ? `I own a property that may fit your buyer for "${title}".` : "I own a property that may fit your buyer.";

const fullNameOf = (first, last) => [first, last].filter(Boolean).join(" ").trim();

const WantContactPanel = ({ agent, wantAdId, title, network, embedded = false }) => {
  const first = agent?.firstName || "the agent";
  const introHref = safeHref(network?.introUrl);
  const agentName = fullNameOf(agent?.firstName, agent?.lastName) || first;
  const [step, setStep] = useState("start");
  const [role, setRole] = useState(null);
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [problem, setProblem] = useState("");
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const openedAt = useRef(Date.now());
  const panelRef = useRef(null);
  const [panelVisible, setPanelVisible] = useState(true);

  useEffect(() => {
    if (!embedded || typeof IntersectionObserver === "undefined" || !panelRef.current) return undefined;
    const observer = new IntersectionObserver(([entry]) => setPanelVisible(entry.isIntersecting), { threshold: 0.2 });
    observer.observe(panelRef.current);
    return () => observer.disconnect();
  }, [embedded]);

  const choose = (next) => {
    setRole(next);
    setErrors({});
    setProblem("");
    if (next === "agent") {
      setStep("agent");
      return;
    }
    setValues((current) => ({
      ...current,
      message: current.message || (next === "owner" ? ownerMessageOf(title) : ""),
    }));
    setStep("form");
  };

  const start = () => {
    openedAt.current = Date.now();
    setStep("who");
  };

  const update = (key) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSending(true);
    setProblem("");
    try {
      const response = await sendBioMessage(agent.slug, {
        name: values.name,
        email: values.email,
        phone: values.phone,
        message: values.message,
        website: values.website,
        address: role === "owner" ? values.address : undefined,
        consent: role === "owner" ? values.consent : undefined,
        role,
        wantAdId,
        elapsedMs: Date.now() - openedAt.current,
      });
      setSentTo(response?.result?.email || values.email.trim());
      setStep("sent");
    } catch (error) {
      const reply = replyOf(error);
      setErrors(reply.errors);
      // Only field errors the form can show may replace the general message.
      setProblem(Object.keys(reply.errors).some((key) => FIELD_KEYS.includes(key)) ? "" : reply.message);
    } finally {
      setSending(false);
    }
  };

  const frame = `want-contact${embedded ? " is-embedded" : ""}`;
  const head = embedded ? null : (
    <div className="want-contact__head">
      <p className="want-contact__title">Contact {agentName}</p>
      <p className="want-contact__sub">
        {[agent?.company, title ? `about "${title}"` : null].filter(Boolean).join(" · ")}
      </p>
    </div>
  );

  if (step === "start") {
    return (
      <section className={frame} aria-label={`Contact ${agentName}`} ref={panelRef}>
        {head}
        <button type="button" className="btn btn-primary want-contact__primary" onClick={start}>
          Contact {first}
        </button>
        {embedded && !panelVisible ? (
          <div className="want-contact__bar">
            <button
              type="button"
              className="btn btn-primary want-contact__bar-btn"
              onClick={() => {
                start();
                window.requestAnimationFrame(() => panelRef.current?.scrollIntoView({ block: "center", behavior: "smooth" }));
              }}
            >
              Contact {first}
            </button>
          </div>
        ) : null}
      </section>
    );
  }

  if (step === "sent") {
    return (
      <section className={frame} role="status" ref={panelRef}>
        <p className="want-contact__title">Check your email</p>
        <p className="want-contact__text">
          We sent a link to <strong>{sentTo}</strong>. Tap it and your details go to {first}.
        </p>
        <p className="want-contact__fine">The link works for 24&nbsp;hours.</p>
        <button type="button" className="want-contact__link" onClick={() => setStep("form")}>
          Wrong email? Change it
        </button>
      </section>
    );
  }

  if (step === "agent") {
    return (
      <section className={frame} ref={panelRef}>
        <p className="want-contact__title">You&apos;re an agent</p>
        <p className="want-contact__text">
          Introductions go through the Buyer Listing Service so both of you can track the conversation.
        </p>
        {introHref ? (
          <>
            <a className="btn btn-primary want-contact__primary" href={introHref}>
              Continue in the {network.name} Buyer Listing&nbsp;Service
            </a>
            <button type="button" className="want-contact__link" onClick={() => choose("other")}>
              Not a {network.name} member? Send {first} a message instead
            </button>
          </>
        ) : (
          <button type="button" className="btn btn-primary want-contact__primary" onClick={() => choose("other")}>
            Send {first} a message
          </button>
        )}
        <button type="button" className="want-contact__link is-quiet" onClick={() => setStep("who")}>
          Back
        </button>
      </section>
    );
  }

  if (step === "who") {
    const options = [
      { key: "owner", label: "I own a property that may fit this buyer" },
      { key: "agent", label: "I'm an agent with a for-sale listing for this buyer" },
      { key: "other", label: `Something else: message ${first}` },
    ];
    return (
      <section className={frame} ref={panelRef}>
        {head}
        <p className="want-contact__question" id="want-contact-who">
          Who are you?
        </p>
        <div className="want-contact__options" role="group" aria-labelledby="want-contact-who">
          {options.map((option) => (
            <button key={option.key} type="button" className="want-contact__option" onClick={() => choose(option.key)}>
              {option.label}
            </button>
          ))}
        </div>
      </section>
    );
  }

  const isOwner = role === "owner";
  return (
    <section className={frame} ref={panelRef}>
      {head}
      <form className="want-contact__form" onSubmit={submit} noValidate>
        <p className="want-contact__question">Your details</p>
        <label htmlFor="want-contact-name">Name</label>
        <input id="want-contact-name" autoComplete="name" value={values.name} onChange={update("name")} aria-invalid={Boolean(errors.name)} />
        {errors.name ? <p className="want-contact__error">{errors.name}</p> : null}
        <label htmlFor="want-contact-email">Email</label>
        <input
          id="want-contact-email"
          type="email"
          autoComplete="email"
          value={values.email}
          onChange={update("email")}
          aria-invalid={Boolean(errors.email)}
        />
        {errors.email ? <p className="want-contact__error">{errors.email}</p> : null}
        <label htmlFor="want-contact-phone">Phone (optional)</label>
        <input id="want-contact-phone" type="tel" autoComplete="tel" value={values.phone} onChange={update("phone")} aria-invalid={Boolean(errors.phone)} />
        {errors.phone ? <p className="want-contact__error">{errors.phone}</p> : null}
        {isOwner ? (
          <>
            <label htmlFor="want-contact-address">Property address (optional)</label>
            <input id="want-contact-address" autoComplete="street-address" value={values.address} onChange={update("address")} />
          </>
        ) : null}
        <label htmlFor="want-contact-message">Message</label>
        <textarea
          id="want-contact-message"
          rows={4}
          value={values.message}
          onChange={update("message")}
          aria-invalid={Boolean(errors.message)}
        />
        {errors.message ? <p className="want-contact__error">{errors.message}</p> : null}
        <input
          className="want-contact__trap"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          name="website"
          value={values.website}
          onChange={update("website")}
        />
        {isOwner ? (
          <label className="want-contact__consent" htmlFor="want-contact-consent">
            <input id="want-contact-consent" type="checkbox" checked={values.consent} onChange={update("consent")} aria-invalid={Boolean(errors.consent)} />
            <span>
              I agree {agentName} may contact me by call, text or email about this buyer.
            </span>
          </label>
        ) : null}
        {errors.consent ? <p className="want-contact__error">{errors.consent}</p> : null}
        {problem ? (
          <p className="want-contact__error" role="alert">
            {problem}
          </p>
        ) : null}
        <button type="submit" className="btn btn-primary want-contact__primary" disabled={sending}>
          {sending ? "Sending..." : `Send to ${first}`}
        </button>
        <p className="want-contact__fine">We confirm your email before {first} gets your details.</p>
        <button type="button" className="want-contact__link is-quiet" onClick={() => setStep("who")}>
          Back
        </button>
      </form>
    </section>
  );
};

export default WantContactPanel;
